import "@/App.css";
import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import Home from "@/pages/Home";
import OffersPage from "@/pages/OffersPage";
import AudiencePage from "@/pages/AudiencePage";
import SubcategoryPage from "@/pages/SubcategoryPage";
import ActivityDetailPage from "@/pages/ActivityDetailPage";
import SportsPage from "@/pages/SportsPage";
import ContactPage from "@/pages/ContactPage";
import PartnerPage from "@/pages/PartnerPage";
import FavoritesPage from "@/pages/FavoritesPage";
import MapAllPage from "@/pages/MapAllPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { SUBCATEGORIES, SPORTS } from "@/constants/categories";

// Kept in memory so the browser keeps them decoded for instant page switches
const PRELOADED = [];
const PRELOAD_SRCS = [
  "/bg/paidia.png", "/bg/enilikes.png", "/bg/paidia_v3.jpg", "/bg/enilikes_v3.jpg", "/brand/home_paidia_card.jpg", "/brand/home_enilikes_card.jpg",
  ...Object.values(SUBCATEGORIES).map((s) => s.image),
  ...Object.values(SPORTS).map((s) => s.image),
].filter(Boolean);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => { if (navType !== "POP") window.scrollTo(0, 0); }, [pathname, navType]);
  return null;
};

function App() {
  useEffect(() => {
    PRELOAD_SRCS.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.decode?.().catch(() => {});
      PRELOADED.push(img);
    });
  }, []);
  return (
    <div className="App">
      <LanguageProvider>
        <FavoritesProvider>
          <AuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/offers" element={<OffersPage />} />
                <Route path="/c/:audience" element={<AudiencePage />} />
                <Route path="/sports/:audience" element={<SportsPage />} />
                <Route path="/s/:audience/:subcat" element={<SubcategoryPage />} />
                <Route path="/age/:group" element={<SubcategoryPage />} />
                <Route path="/activity/:id" element={<ActivityDetailPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/partner" element={<PartnerPage />} />
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/map" element={<MapAllPage />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </FavoritesProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
